<template>
    <div>       
        <!-- Add Item Modal -->
        <b-modal
            v-model="createModal"
            title="Add Item"
            @hidden="onCreateModalHidden"
            centered
            size="lg"
            :hide-footer="showCreateSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 300px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showCreateSuccess" :message="skipRedirectOnCreate ? createSuccessMessage : createSuccessMessageRedirect"></save-success>
                    <product-detail-form
                        v-else
                        ref="createForm"
                        @save-complete="handleCreateComplete"
                        create-new
                        no-refresh-on-update
                        hide-save-checkbox
                        edit-mode
                        class="w-100"
                    ></product-detail-form>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button 
                    variant="minimal-secondary" 
                    @click="cancel()"
                >
                    Cancel
                </b-button>
                <b-button 
                    v-if="!hideSaveClose"
                    variant="primary" 
                    :disabled="!tuCanCreate"
                    @click="createItemForm({skipRedirect: true})"
                >
                    <span v-html="saveCloseHtml"></span>
                </b-button>
                <b-button 
                    v-if="!hideSaveRedirect"
                    variant="primary" 
                    :disabled="!tuCanCreate"
                    @click="createItemForm({skipRedirect: false})"
                >
                    <span v-html="saveRedirectHtml"></span>
                </b-button>
            </template>
        </b-modal>

        <!-- UPDATE MODAL -->
        <b-modal
            v-model="updateModal"
            title="Item Details"
            @hidden="onUpdateModalHidden"
            centered
            size="lg"
            :hide-footer="showUpdateSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 300px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showUpdateSuccess" :message="updateSuccessMessage"></save-success>
                    <product-detail-form
                        v-else
                        :item-pk="updateItemId"
                        ref="updateForm"
                        @update-complete="handleUpdateComplete"
                        no-refresh-on-update
                        class="w-100"
                    ></product-detail-form>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button v-if="editMode" variant="outline-secondary" @click="cancelEditMode">Cancel Edits</b-button>
                <b-button v-else variant="outline-secondary" @click="cancel()">Close</b-button>
                <b-button v-if="editMode" variant="success" :disabled="!tuCanUpdate" @click="updateItemForm">Save Edits</b-button>
                <b-button v-else variant="secondary" :disabled="!tuCanUpdate" @click="enterEditMode">Edit</b-button>
            </template>
        </b-modal>

    </div>
</template>

<script>
// modal to add / update Item basic data - holds ItemDetailForm

import ProductDetailForm from './ProductDetailForm';
import SaveSuccess from './functional-comps/SaveSuccess';
import { currentUserInfoMixin, tenantInfoMixin } from "../js/custom-vue-mixins";

export default {
    name: 'ItemCreateUpdateModal',
    components: {
        ProductDetailForm,
        SaveSuccess,
    },
    mixins: [
        currentUserInfoMixin,
        tenantInfoMixin,
    ],
    props: {
        redirectAfterCreate: {
            // NO LONGER BEING USED -- replaced by having 2 separate buttons that control skipRedirectOnCreate
            type: Boolean,
            default: false,
        },
        // the text (as HTML) and visibility of the multiple save buttons
        saveCloseHtml: {
            type: String,
            default: 'Save &amp; close',
        },
        hideSaveClose: { // should we hide the 'save and close' button?
            type: Boolean,
            default: false,
        },
        saveRedirectHtml: {
            type: String,
            default: 'Save &amp; continue editing &rarr;',
        },
        hideSaveRedirect: {  // should we hide the 'save and redirect' button?
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            createModal: false,
            updateModal: false,
            editMode: false,
            updateItemId: null,
            showUpdateSuccess: false,  
            showCreateSuccess: false,
            updateSuccessMessage: "Updated successfully",
            createSuccessMessage: "Item saved",
            createSuccessMessageRedirect: "Item saved.  Let's add some details...",
            successMessageTime: 2000,
            skipRedirectOnCreate: false,
        }
    },
    computed: {
        tuCanCreate() {
            // can current TU create new ones?
            if (this.$getTenantFlag('allow_any_internal_write_products', false)) {
                // check tenant flag for now... in future, we will have actual permissions
                return this.$currentUserInfo?.is_internal || false;
            }
            return this.$currentUserInfo?.is_paid_seat || false;  // replace with specific perm check when ready
        },
        tuCanUpdate() {
            // can current TU update existing?
            if (this.$getTenantFlag('allow_any_internal_write_products', false)) {
                // check tenant flag for now... in future, we will have actual permissions
                return this.$currentUserInfo?.is_internal || false;
            }
            return this.$currentUserInfo?.is_paid_seat || false;  // replace with specific perm check when ready
        },
    },
    methods: {
        // below are 2 main trigger functions -- can call these publicly
        startUpdate: function(updateId) {
            this.updateItemId = updateId;
            this.updateModal = true;
        },
        startCreate: function() {
            this.createModal = true;
        },
        handleUpdateComplete: function(updatedObject) {
            // updatedObject MAY be the newly updated object returned from API (in response.data) - depends on your endpoint
            console.log('Updated response data below');
            console.log(updatedObject);
            this.showUpdateSuccess = true;
            this.$emit('update-complete', updatedObject);  // relay the update-complete event from form
            setTimeout(() => {
                // should we redirect to detail page after update?  if so, put here
                this.updateModal = false;
                this.$emit('update-modal-hide');  // tell parent we're closing
            }, this.successMessageTime);
        },
        onUpdateModalHidden: function() {
            this.editMode = false;
            this.showUpdateSuccess = false;
            this.$emit('update-modal-hidden');  // tell the parent we're hidden
        },
        handleCreateComplete: function(newObject) {
            // newObject should be the newly created object returned from API (in response.data)
            console.log('From parent component, we got this newObject passed up from save-complete event');
            console.log(newObject);
            this.showCreateSuccess = true;
            this.$emit('create-complete', newObject);  // relay the save-complete event from form
            setTimeout(() => {  // after the timeout, do the redirect if we have the URL
                let redirectUrl = newObject.url;
                if (redirectUrl && !this.skipRedirectOnCreate) {
                    window.location.href = redirectUrl;  // THE REDIRECT
                } else {  // if we didn't have redirect URL just hide the modal and emit event to tell parent we're done
                    this.createModal = false;
                    this.$emit('create-modal-hide');  // tell parent we're closing modal
                }
            }, this.successMessageTime);
        },
        onCreateModalHidden: function() {
            this.editMode = false;  // just in case?
            this.showCreateSuccess = false;
            this.skipRedirectOnCreate = false;  // reset this
            this.$emit('create-modal-hidden');  // tell the parent we're hidden
        },
        // NEXT 4 METHODS CALL METHOD ON CHILD FORM COMPONENT
        enterEditMode: function() {
            // set local edit mode and call cancelEditMode on child form
            this.editMode = true;
            this.$refs.updateForm.enterEditMode();
        },
        cancelEditMode: function() {
            // set local edit mode and call cancelEditMode on child form
            this.editMode = false;
            this.$refs.updateForm.cancelEditMode();
        },
        createItemForm: function(params) {
            const skipRedirect = !!params.skipRedirect;  // pass in {skipRedirect: true} to skip the redirect after save is complete
            this.skipRedirectOnCreate = skipRedirect;
            this.$refs.createForm.saveNewItem();
        },
        updateItemForm: function() {
            // call update method on child form
            // no need to set local editMode value here... let events from child component set that
            // but make sure you watch for them with @edit-on and @edit-off
            this.$refs.updateForm.updateItemData();
        },
    }
}
</script>

<style>

</style>